import { useState } from "react";

import { identity } from "lodash";

import { SWReactIcons } from "@similarweb/icons";
import { Button, SearchInput } from "@similarweb/ui-components";

import { translate } from "app/filters/i18nFilter";
import useEnrichmentList from "app/pages/sales-intelligence/modules/enrichment/queries/useEnrichmentList";
import {
    CLOSE,
    ENRICHMENT_HISTORY_TITLE,
    NO_RESULTS,
    SEARCH_CSV_PLACEHOLDER,
} from "app/pages/sales-intelligence/modules/enrichment/utils/i18n/modal.texts";
import { ExportTablePreloader } from "app/pages/sales-intelligence/pages/management/components/common/Preloaders/ExportTablePreloader";
import { WithKeysInput } from "app/pages/sales-intelligence/sub-modules/management/hocs/withKeysInput";
import { allTrackers } from "app/services/track/track";
import type { IProModalCustomStyles } from "pro-features/components/Modals/src/ProModal";
import { ProModal } from "pro-features/components/Modals/src/ProModal";

import { EnrichedItem } from "./components/EnrichedItem";
import {
    HistoryFooter,
    HistoryTitle,
    StyledEnrichedContent,
    StyledEnrichmentHistoryWrapper,
    StyledModalBody,
    StyledNoResultsWrapper,
    StyledSearchContainer,
} from "./style";

type EnrichmentHistoryModalProps = {
    onOpenStateChange(isOpen: boolean): void;
    isOpen: boolean;
};

const CUSTOM_MODAL_STYLES: IProModalCustomStyles = {
    content: { padding: 0, width: "825px", transition: "0.5s ease-out" },
};

export const EnrichmentHistoryModal = ({ onOpenStateChange, isOpen }: EnrichmentHistoryModalProps) => {
    const { data, isLoading } = useEnrichmentList();

    const [searchTerm, setSearchTerm] = useState("");
    let searchTimer: number | undefined | any = undefined;

    const onClose = () => {
        onOpenStateChange(false);
        setSearchTerm("");
    };
    const renderContent = () => {
        if (isLoading) {
            return <ExportTablePreloader rowsNum={12} />;
        }

        const renderItems = (data ?? [])
            .filter((enriched) => {
                if (searchTerm) {
                    return enriched.fileName.toLowerCase().includes(searchTerm.toLowerCase());
                }
                return true;
            })
            .map((enrichedState) => {
                return (
                    <EnrichedItem
                        key={enrichedState.id}
                        enrichedState={enrichedState}
                    />
                );
            });

        return renderItems.length ? (
            renderItems
        ) : (
            <StyledNoResultsWrapper>
                <SWReactIcons
                    iconName="no-search-results"
                    size="md"
                />
                <div>{translate(NO_RESULTS)}</div>
            </StyledNoResultsWrapper>
        );
    };

    const onSearch = (term: string) => setSearchTerm(term);

    const onKeyUp = () => {
        clearTimeout(searchTimer);

        searchTimer = setTimeout(() => {
            allTrackers.dispatchTrackEvent("enrichment archive", "use searchbar", `search archive/${searchTerm}`);
        }, 5000);
    };

    const onKeyDown = () => clearTimeout(searchTimer);

    return (
        <ProModal
            data-automation="enrichment-history-modal"
            customStyles={CUSTOM_MODAL_STYLES}
            isOpen={isOpen}
            showCloseIcon={false}
            shouldCloseOnOverlayClick
        >
            <WithKeysInput
                onEnter={identity}
                onEscape={onClose}
            >
                <StyledEnrichmentHistoryWrapper>
                    <StyledModalBody>
                        <HistoryTitle>{translate(ENRICHMENT_HISTORY_TITLE)}</HistoryTitle>
                        <StyledSearchContainer>
                            <SearchInput
                                onKeyUp={onKeyUp}
                                onKeyDown={onKeyDown}
                                autoFocus
                                placeholder={translate(SEARCH_CSV_PLACEHOLDER)}
                                isLoading={isLoading}
                                onChange={onSearch}
                            />
                        </StyledSearchContainer>

                        <StyledEnrichedContent>{renderContent()}</StyledEnrichedContent>
                    </StyledModalBody>

                    <HistoryFooter>
                        <Button
                            onClick={onClose}
                            type="flat"
                        >
                            {translate(CLOSE)}
                        </Button>
                    </HistoryFooter>
                </StyledEnrichmentHistoryWrapper>
            </WithKeysInput>
        </ProModal>
    );
};
